<template>
  <div class="port-config-page">
    <!-- 客户端配置 -->
    <el-card class="tunnel-card" shadow="hover">
      <template #header>
        <div class="card-header">
          <span>{{ $t('officialClient.serverSettings') }}</span>
          <div class="header-actions">
            <el-button type="info" size="small" @click="showLogDialog">
              <el-icon><Document /></el-icon>
              {{ $t('officialClient.viewLogs') }}
            </el-button>
          </div>
        </div>
      </template>

      <div class="table-responsive">
        <table class="config-table">
          <thead>
            <tr>
              <th>{{ $t('officialClient.serverAddress') }}</th>
              <th>{{ $t('officialClient.serverPort') }}</th>
              <th>{{ $t('officialClient.associatedUser') }}</th>
              <th>{{ $t('common.status') }}</th>
              <th>{{ $t('common.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td data-label="服务器地址">{{ clientConfig.officialAddr }}</td>
              <td data-label="服务器端口号">{{ clientConfig.officialPort }}</td>
              <td data-label="关联的用户">{{ clientConfig.bindUsername || '-' }}</td>
              <td data-label="状态">
                <span class="status-tag" :class="clientConfig.officialSwitch ? 'enabled' : 'disabled'">
                  {{ clientConfig.status }}
                </span>
              </td>
              <td data-label="操作" class="action-cell">
                <div class="action-buttons">
                  <!-- 编辑按钮始终显示 -->
                  <el-button
                    v-if="!clientConfig.officialSwitch"
                    type="primary"
                    size="small"
                    @click="showClientEditDialog"
                    :disabled="dataLoading"
                  >
                    {{ $t('officialClient.edit') }}
                  </el-button>

                  <!-- 状态相关按钮 -->
                  <template v-if="!clientConfig.officialSwitch">
                    <el-button
                      type="success"
                      size="small"
                      @click="startClient"
                      :loading="dataLoading"
                    >
                      {{ $t('officialClient.start') }}
                    </el-button>
                  </template>
                  <template v-else>
                    <el-button
                      type="danger"
                      size="small"
                      @click="stopClient"
                      :loading="dataLoading"
                    >
                      {{ $t('officialClient.stop') }}
                    </el-button>
                  </template>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </el-card>

    <!-- 加载状态 -->
    <el-skeleton 
      v-if="dataLoading && mappingList.length === 0" 
      :rows="5" 
      animated 
      class="skeleton-card"
    />

    <!-- 错误提示 -->
    <el-alert
      v-else-if="error"
      :title="error"
      type="error"
      show-icon
      class="error-alert"
    >
      <template #action>
        <el-button size="small" type="danger" @click="refreshData">{{ $t('officialClient.retry') }}</el-button>
      </template>
    </el-alert>

    <!-- 端口映射列表 -->
    <el-card v-else class="mapping-card" shadow="hover">

<!--
      <template #header>
        <div class="card-header">
          <span>自动映射列表({{ connectServerIP }})</span>
          <el-button type="primary" size="small" @click="showAddMappingDialog">添加映射</el-button>
        </div>
      </template>
-->

      <div class="table-responsive">
        <table class="mapping-table">
          <thead>
            <tr>
              <th>{{ $t('officialClient.mappingName') }}</th>
              <th>{{ $t('officialClient.internalAddress') }}</th>
              <th>{{ $t('officialClient.internalPort') }}</th>
              <th>{{ $t('officialClient.mappingPort') }}</th>
              <th>{{ $t('officialClient.protocol') }}</th>
              <th>{{ $t('common.status') }}</th>
              <th>{{ $t('common.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in mappingList" :key="row.name">
              <td data-label="名称">{{ row.name }}</td>
              <td data-label="内网地址">{{ row.targetIP }}</td>
              <td data-label="内网端口">{{ row.targetPort }}</td>
              <td data-label="映射端口">{{ row.mappingPort === 0 ? $t('officialClient.autoAssigned') : row.mappingPort }}</td>
              <td data-label="协议">
                <span class="protocol-tag" :class="row.protocol">
                  {{ row.protocol.toUpperCase() }}
                </span>
              </td>
              <td data-label="状态">
                <div class="status-wrapper">
                  <span class="status-tag" :class="row.switch ? 'enabled' : 'disabled'">
                    {{ row.switch ? $t('officialClient.running') : $t('officialClient.stopped') }}
                  </span>
                  <span v-if="row.status && row.status !== (row.switch ? $t('officialClient.running') : $t('officialClient.stopped'))" class="status-message">
                    {{ row.status }}
                  </span>
                </div>
              </td>
              <td data-label="操作" class="action-cell">
                <div class="action-buttons">
                  <template v-if="row.switch">
                    <el-button
                      type="danger"
                      size="small"
                      @click="stopMapping(row)"
                      :loading="row.loading"
                    >
                      {{ $t('officialClient.stop') }}
                    </el-button>
                    <el-button
                      v-if="false"
                      type="primary"
                      size="small"
                      @click="showMappingEditDialog(row)"
                    >
                      {{ $t('officialClient.edit') }}
                    </el-button>
                  </template>
                  <template v-else>
                    <el-button
                      v-if="false"
                      type="primary"
                      size="small"
                      @click="showMappingEditDialog(row)"
                    >
                      {{ $t('officialClient.edit') }}
                    </el-button>
                    <el-button
                      type="success"
                      size="small"
                      @click="updateMapping(row)"
                      :loading="row.loading"
                    >
                      {{ $t('officialClient.start') }}
                    </el-button>
                  </template>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- 空状态 -->
      <div v-if="mappingList.length === 0" class="empty-mapping">
        <el-empty :description="$t('officialClient.noMapping')" :image-size="100">
          <el-button type="primary" @click="showAddMappingDialog" size="small">{{ $t('officialClient.addMapping') }}</el-button>
        </el-empty>
      </div>
    </el-card>

    <!-- 客户端配置弹窗 -->
    <el-dialog
      v-model="clientDialogVisible"
      :title="$t('officialClient.editClientConfig')"
      :width="isMobile ? '95%' : '500px'"
      :before-close="handleClientDialogClose"
      @close="resetClientForm"
      :destroy-on-close="true"
      class="settings-dialog"
    >
      <el-form
        ref="clientFormRef"
        :model="clientEditForm"
        :rules="clientRules"
        :label-position="isMobile ? 'top' : 'right'"
        label-width="100px"
      >
        <el-form-item :label="$t('officialClient.serverAddress')" prop="officialAddr">
          <el-input
            v-model="clientEditForm.officialAddr"
            :placeholder="$t('officialClient.enterUsername')"
            :disabled="true"
          />
          <div class="form-tip">{{ $t('officialClient.serverAddressNotEditable') }}</div>
        </el-form-item>

        <el-form-item :label="$t('officialClient.serverPort')" prop="officialPort">
          <el-input-number
            v-model="clientEditForm.officialPort"
            :min="1"
            :max="65535"
            controls-position="right"
            style="width: 100%"
            :placeholder="$t('officialClient.internalPortPlaceholder')"
            :disabled="true"
          />
          <div class="form-tip">{{ $t('officialClient.serverPortNotEditable') }}</div>
        </el-form-item>

        <el-form-item :label="$t('officialClient.controlToken')" prop="controlToken">
          <el-input
            v-model="clientEditForm.controlToken"
            show-password
            :placeholder="$t('officialClient.enterControlToken')"
            :disabled="true"
          />
          <div class="form-tip">{{ $t('officialClient.controlTokenNotEditable') }}</div>
        </el-form-item>

        <el-form-item :label="$t('officialClient.usernameLabel')" prop="bindUsername">
          <el-input
            v-model="clientEditForm.bindUsername"
            :placeholder="$t('officialClient.enterUsername')"
          />
        </el-form-item>
      </el-form>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="clientDialogVisible = false">{{ $t('officialClient.cancel') }}</el-button>
          <el-button type="primary" @click="updateClient" :loading="dataLoading">{{ $t('officialClient.confirm') }}</el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 映射编辑/添加弹窗 -->
    <el-dialog
      v-model="mappingDialogVisible"
      :title="mappingDialogType === 'add' ? $t('officialClient.addNewMapping') : $t('officialClient.editMapping')"
      :width="isMobile ? '95%' : '500px'"
      @close="resetMappingForm"
      :destroy-on-close="true"
      class="settings-dialog"
    >
      <el-form
        ref="mappingFormRef"
        :model="mappingEditForm"
        :rules="mappingRules"
        :label-position="isMobile ? 'top' : 'right'"
        label-width="120px"
      >
        <el-form-item :label="$t('officialClient.nameLabel')" prop="name">
          <el-input
            v-model="mappingEditForm.name"
            :placeholder="$t('officialClient.namePlaceholder')"
            maxlength="15"
            show-word-limit
          />
        </el-form-item>

        <el-form-item :label="$t('officialClient.internalAddressLabel')" prop="targetIP">
          <el-input
            v-model="mappingEditForm.targetIP"
            :placeholder="$t('officialClient.internalIpPlaceholder')"
          />
        </el-form-item>

        <el-form-item :label="$t('officialClient.internalPortLabel')" prop="targetPort">
          <el-input-number
            v-model="mappingEditForm.targetPort"
            :min="1"
            :max="65535"
            controls-position="right"
            style="width: 100%"
            :placeholder="$t('officialClient.internalPortPlaceholder')"
          />
        </el-form-item>

        <el-form-item :label="$t('officialClient.mappingPortLabel')" prop="mappingPort">
          <el-input-number
            v-model="mappingEditForm.mappingPort"
            :min="0"
            :max="65535"
            controls-position="right"
            style="width: 100%"
            :placeholder="$t('officialClient.mappingPortPlaceholder')"
          />
          <div class="form-tip">{{ $t('officialClient.autoAssignPort') }}</div>
        </el-form-item>

        <el-form-item :label="$t('officialClient.protocolLabel')" prop="protocol">
          <el-radio-group v-model="mappingEditForm.protocol">
            <el-radio value="tcp">{{ $t('officialClient.tcp') }}</el-radio>
            <el-radio value="udp">{{ $t('officialClient.udp') }}</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="mappingDialogVisible = false">{{ $t('officialClient.cancel') }}</el-button>
          <el-button type="primary" @click="saveMapping" :loading="dataLoading">{{ $t('officialClient.confirm') }}</el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 日志查看对话框 -->
    <el-dialog
      v-model="logDialogVisible"
      :title="$t('officialClient.runningLogs')"
      :width="isMobile ? '95%' : '900px'"
      :close-on-click-modal="false"
      :destroy-on-close="true"
      class="settings-dialog log-dialog"
    >
      <div class="log-toolbar">
        <div class="log-stats">
          <span>{{ $t('officialClient.totalLogs', { count: logTotal }) }}</span>
          <div class="auto-refresh-control">
            <span class="refresh-label">{{ $t('officialClient.autoRefresh') }}</span>
            <el-switch
              v-model="autoRefreshEnabled"
              :active-value="true"
              :inactive-value="false"
              @change="toggleAutoRefresh"
              size="small"
              active-color="#409eff"
            />
            <span class="refresh-interval" v-if="autoRefreshEnabled">{{ $t('officialClient.refreshInterval') }}</span>
          </div>
        </div>
        <div class="log-actions">
          <el-button
            type="success"
            size="small"
            @click="downloadAllLogs"
            :loading="logDownloading"
            :icon="Download"
          >
            {{ $t('officialClient.downloadAllLogs') }}
          </el-button>
          <el-button
            type="primary"
            size="small"
            @click="refreshLogs"
            :loading="logLoading"
            :icon="Refresh"
          >
            {{ $t('officialClient.refreshNow') }}
          </el-button>
          <el-button
            type="danger"
            size="small"
            @click="clearLogs"
            :loading="logClearing"
          >
            {{ $t('officialClient.clearLogs') }}
          </el-button>
        </div>
      </div>

      <div class="log-content" ref="logContentRef">
        <div v-if="logsData.length === 0" class="empty-log">
          <el-icon><InfoFilled /></el-icon>
          <p>{{ $t('officialClient.noLogs') }}</p>
        </div>
        <div v-else class="log-list">
          <div
            v-for="(log, index) in logsData"
            :key="index"
            class="log-item"
            :class="`log-level-${log.Level.toLowerCase()}`"
          >
            <span class="log-time">{{ log.Time }}</span>
            <span class="log-level" :class="`level-${log.Level.toLowerCase()}`">
              [{{ log.Level }}]
            </span>
            <span class="log-message">{{ log.Content }}</span>
          </div>
        </div>
      </div>

      <div class="log-pagination" v-if="logTotal > 0">
        <el-pagination
          v-model:current-page="logCurrentPage"
          v-model:page-size="logPageSize"
          :page-sizes="[20, 50, 100, 200]"
          :total="logTotal"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleLogSizeChange"
          @current-change="handleLogPageChange"
          small
        />
      </div>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="logDialogVisible = false">{{ $t('officialClient.close') }}</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { ElMessage, ElMessageBox } from 'element-plus'
import { API_BASE_URL } from '@/config';
import { useRouter } from 'vue-router'
import { Document, InfoFilled } from '@element-plus/icons-vue'

export default {
  name: 'PortConfig',
  components: { Document, InfoFilled },
  setup() {
    const router = useRouter()
    const { t } = useI18n()
    const isMobile = ref(false)
    
    // 检测移动端
    const checkMobile = () => {
      isMobile.value = window.innerWidth < 768
    }
    
    const dataLoading = ref(false)
    const error = ref(null)
    
    // 定时器
    const timer = ref(null)
    const logRefreshInterval = ref(null)
    
    // 表单引用
    const clientFormRef = ref(null)
    const mappingFormRef = ref(null)
    
    // 弹窗控制
    const clientDialogVisible = ref(false)
    const mappingDialogVisible = ref(false)
    const mappingDialogType = ref('add') // 'add' 或 'edit'
    
    // 日志对话框相关
    const logDialogVisible = ref(false)
    const logLoading = ref(false)
    const logClearing = ref(false)
    const logDownloading = ref(false)
    const logsData = ref([])
    const logTotal = ref(0)
    const logCurrentPage = ref(1)
    const logPageSize = ref(50)
    const logContentRef = ref(null)
    const autoRefreshEnabled = ref(true) // 默认开启自动刷新
    
    // 客户端配置数据
    const clientConfig = reactive({
      officialAddr: "",
      officialPort: 0,
      officialSwitch: false,
      bindUsername: "",
      controlToken: "",
      status: "未连接"
    })
    
    // 客户端编辑表单
    const clientEditForm = reactive({
      officialAddr: "",
      officialPort: 0,
      controlToken: "",
      bindUsername: ""
    })
    
    // 映射编辑表单
    const mappingEditForm = reactive({
      name: '',
      targetIP: '',
      targetPort: 80,
      mappingPort: 0,
      protocol: 'tcp',
      originalName: '' // 用于编辑时保存原名称
    })
    
    // 客户端表单验证规则
    const clientRules = {
      bindUsername: [
        { required: false, message: t('officialClient.enterUsername'), trigger: 'blur' }
      ]
    }

    // 映射表单验证规则
    const mappingRules = {
      name: [
        { required: true, message: t('officialClient.enterName'), trigger: 'blur' },
        { min: 5, max: 15, message: t('officialClient.nameLengthError'), trigger: 'blur' }
      ],
      targetIP: [
        { required: true, message: t('officialClient.enterInternalAddress'), trigger: 'blur' },
        { validator: (rule, value, callback) => {
          if (!validateIP(value)) {
            callback(new Error(t('officialClient.internalIpFormatError')))
          } else {
            callback()
          }
        }, trigger: 'blur' }
      ],
      targetPort: [
        { required: true, message: t('officialClient.enterInternalPort'), trigger: 'blur' },
        { type: 'number', min: 1, max: 65535, message: t('officialClient.internalPortRangeError'), trigger: 'blur' }
      ],
      mappingPort: [
        { required: true, message: t('officialClient.enterMappingPort'), trigger: 'blur' },
        { type: 'number', min: 0, max: 65535, message: t('officialClient.mappingPortRangeError'), trigger: 'blur' }
      ],
      protocol: [
        { required: true, message: t('officialClient.selectProtocol'), trigger: 'change' }
      ]
    }
    
    const mappingList = ref([])
    const connectServerIP = ref('')
    
    // 验证IP地址
    const validateIP = (ip) => {
      const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/
      return ipRegex.test(ip) || ip === 'localhost' || ip === '127.0.0.1'
    }
    
    // 启动定时器（5秒刷新）
    const startTimer = () => {
      if (timer.value) return;
      
      timer.value = setInterval(() => {
        refreshData()
      }, 5000)
    }
    
    // 停止定时器
    const stopTimer = () => {
      if (timer.value) {
        clearInterval(timer.value)
        timer.value = null
      }
    }
    
    // 刷新数据
    const refreshData = async () => {
      if (dataLoading.value) return;
      
      dataLoading.value = true
      error.value = null
      
      try {
        const response = await fetch(`${API_BASE_URL}/local-tunnel-client`, {
          method: 'GET',
          credentials: 'include'
        })
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        
        const data = await response.json()

        if (data.errCode !== 0) {
          if (data.errCode === 1000) {
            ElMessage.warning(t('common.notLoggedIn'))
            router.push('/user-login?callback=official-client')
            return
          }
          throw new Error(data.errMsg || t('officialClient.fetchFailed'))
        }
        
        // 更新客户端配置数据
        if (data.data) {
          Object.assign(clientConfig, {
            officialAddr: data.data.officialAddr,
            officialPort: data.data.officialPort,
            officialSwitch: data.data.switch,
            bindUsername: data.data.bindUsername,
            controlToken: data.data.controlToken,
            status: data.data.status || (data.data.switch ? t('officialClient.connected') : t('officialClient.notConnected'))
          })
        }

        connectServerIP.value = data.connectServerIP

        // 更新映射列表
        if (data.data && data.data.tunnelMappingPort) {
          mappingList.value = data.data.tunnelMappingPort.map(item => ({
            ...item,
            protocol: item.protocol === 0 ? 'tcp' : 'udp',
            loading: false,
            switch: item.switch || false,
            status: item.status || (item.switch ? t('officialClient.running') : t('officialClient.stopped'))
          }))
        }
      } catch (err) {
        console.error(t('officialClient.refreshDataFailed'), err)
        error.value = err.message
        ElMessage.error(t('officialClient.refreshFailed') + err.message)
      } finally {
        dataLoading.value = false
      }
    }
    
    // 显示客户端编辑弹窗
    const showClientEditDialog = () => {
      Object.assign(clientEditForm, {
        officialAddr: clientConfig.officialAddr,
        officialPort: clientConfig.officialPort,
        controlToken: clientConfig.controlToken,
        bindUsername: clientConfig.bindUsername
      })
      clientDialogVisible.value = true
    }
    
    // 重置客户端表单
    const resetClientForm = () => {
      if (clientFormRef.value) {
        clientFormRef.value.clearValidate()
      }
    }
    
    // 处理客户端弹窗关闭
    const handleClientDialogClose = (done) => {
      resetClientForm()
      done()
    }
    
    // 显示添加映射弹窗
    const showAddMappingDialog = () => {
      mappingDialogType.value = 'add'
      Object.assign(mappingEditForm, {
        name: '',
        targetIP: '127.0.0.1',
        targetPort: 80,
        mappingPort: 0,
        protocol: 'tcp',
        originalName: ''
      })
      mappingDialogVisible.value = true
    }
    
    // 显示编辑映射弹窗
    const showMappingEditDialog = (row) => {
      mappingDialogType.value = 'edit'
      Object.assign(mappingEditForm, {
        name: row.name,
        targetIP: row.targetIP,
        targetPort: row.targetPort,
        mappingPort: row.mappingPort,
        protocol: row.protocol,
        originalName: row.name
      })
      mappingDialogVisible.value = true
    }
    
    // 重置映射表单
    const resetMappingForm = () => {
      if (mappingFormRef.value) {
        mappingFormRef.value.clearValidate()
      }
    }
    
    // 保存映射（添加或编辑）
    const saveMapping = async () => {
      if (!mappingFormRef.value) return
      
      await mappingFormRef.value.validate(async (valid) => {
        if (!valid) return
        
        dataLoading.value = true
        try {
          const formData = new URLSearchParams();
          formData.append('name', mappingEditForm.name);
          formData.append('targetIP', mappingEditForm.targetIP);
          formData.append('targetPort', mappingEditForm.targetPort);
          formData.append('mappingPort', mappingEditForm.mappingPort);
          formData.append('protocol', mappingEditForm.protocol === 'tcp' ? 0 : 1)
          
          // 如果是编辑模式且名称已修改，添加原名称参数
          if (mappingDialogType.value === 'edit' && mappingEditForm.name !== mappingEditForm.originalName) {
            formData.append('oldName', mappingEditForm.originalName);
          }
          
          const response = await fetch(`${API_BASE_URL}/update-local-tunnel-mapping`, {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: formData.toString()
          })
          
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
          }
          
          const data = await response.json()
          
          if (data.errCode !== 0) {
            throw new Error(data.errMsg || t('officialClient.mappingSaveFailed'))
          }

          mappingDialogVisible.value = false
          ElMessage.success(mappingDialogType.value === 'add' ? t('officialClient.mappingAdded') : t('officialClient.mappingUpdated'))
          refreshData()
        } catch (error) {
          ElMessage.error(t('officialClient.saveFailed') + error.message)
        } finally {
          dataLoading.value = false
        }
      })
    }

    // 更新映射（启动）
    const updateMapping = async (row) => {
      // 验证输入
      if (!validateIP(row.targetIP)) {
        ElMessage.warning(t('officialClient.internalIpFormatError'))
        return
      }

      if (!row.targetPort || row.targetPort < 1 || row.targetPort > 65535) {
        ElMessage.warning(t('officialClient.internalPortRangeError'))
        return
      }

      if (row.mappingPort < 0 || row.mappingPort > 65535) {
        ElMessage.warning(t('officialClient.mappingPortRangeError'))
        return
      }

      row.loading = true
      try {
        const formData = new URLSearchParams();
        formData.append('name', row.name);
        formData.append('targetIP', row.targetIP);
        formData.append('targetPort', row.targetPort);
        formData.append('mappingPort', row.mappingPort);
        formData.append('protocol', row.protocol === 'tcp' ? 0 : 1)

        const response = await fetch(`${API_BASE_URL}/update-local-tunnel-mapping`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: formData.toString()
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()

        if (data.errCode !== 0) {
          throw new Error(data.errMsg || t('officialClient.mappingStartFailed'))
        }

        row.switch = true
        row.status = data.realPort ? t('officialClient.mappingSuccess', { port: data.realPort }) : t('officialClient.running')
        ElMessage.success(t('officialClient.mappingStarted'))
      } catch (error) {
        ElMessage.error(t('officialClient.startFailed') + error.message)
      } finally {
        row.loading = false
      }
    }

    // 停止映射
    const stopMapping = async (row) => {
      try {
        await ElMessageBox.confirm(
          t('officialClient.confirmStopMapping', { name: row.name }),
          t('officialClient.prompt'),
          {
            confirmButtonText: t('officialClient.confirmText'),
            cancelButtonText: t('officialClient.cancelText'),
            type: 'warning'
          }
        );
      } catch (error) {
        return;
      }

      row.loading = true
      try {
        const formData = new URLSearchParams();
        formData.append('name', row.name);

        const response = await fetch(`${API_BASE_URL}/stop-local-tunnel-mapping`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: formData.toString()
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()

        if (data.errCode !== 0) {
          throw new Error(data.errMsg || t('officialClient.mappingStopFailed'))
        }

        row.switch = false
        row.status = t('officialClient.mappingStoppedMsg')
        ElMessage.success(t('officialClient.mappingStopped'))
      } catch (error) {
        ElMessage.error(t('officialClient.stopFailed') + error.message)
      } finally {
        row.loading = false
      }
    }

    // 更新客户端连接
    const updateClient = async () => {
      if (!clientFormRef.value) return

      await clientFormRef.value.validate(async (valid) => {
        if (!valid) return

        if (!clientEditForm.bindUsername) {
          try {
            await ElMessageBox.confirm(t('officialClient.usernameEmpty'), t('officialClient.prompt'), {
              confirmButtonText: t('officialClient.confirmText'),
              cancelButtonText: t('officialClient.cancelText'),
              type: 'warning'
            });
          } catch (error) {
            return;
          }
        }

        dataLoading.value = true
        try {
          const formData = new URLSearchParams();
          formData.append('officialAddr', clientEditForm.officialAddr);
          formData.append('officialPort', clientEditForm.officialPort);
          formData.append('controlToken', clientEditForm.controlToken);
          formData.append('bindUsername', clientEditForm.bindUsername);
          formData.append('token', "default");

          const response = await fetch(`${API_BASE_URL}/update-local-tunnel-client`, {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: formData.toString()
          })

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
          }

          const data = await response.json()
          
          if (data.errCode !== 0) {
            throw new Error(data.errMsg || t('officialClient.updateClientConfigFailed'))
          }

          // 更新客户端配置
          Object.assign(clientConfig, {
            officialAddr: clientEditForm.officialAddr,
            officialPort: clientEditForm.officialPort,
            controlToken: clientEditForm.controlToken,
            bindUsername: clientEditForm.bindUsername,
            officialSwitch: true,
            status: t('officialClient.connected')
          })

          clientDialogVisible.value = false
          ElMessage.success(t('officialClient.clientConfigUpdated'))

          refreshData()
        } catch (error) {
          ElMessage.error(t('officialClient.updateFailed') + error.message)
        } finally {
          dataLoading.value = false
        }
      })
    }

    // 启动客户端
    const startClient = async () => {
      if (!clientConfig.officialAddr) {
        ElMessage.warning(t('officialClient.serverAddressRequired'))
        return
      }

      if (!clientConfig.officialPort || clientConfig.officialPort < 1 || clientConfig.officialPort > 65535) {
        ElMessage.warning(t('officialClient.serverPortRangeError'))
        return
      }

      if (!clientConfig.controlToken) {
        ElMessage.warning(t('officialClient.controlTokenRequired'))
        return
      }

      try {
        await ElMessageBox.confirm(t('officialClient.confirmStartClient'), t('officialClient.prompt'), {
          confirmButtonText: t('officialClient.confirmText'),
          cancelButtonText: t('officialClient.cancelText'),
          type: 'info'
        });
      } catch (error) {
        return;
      }

      dataLoading.value = true
      try {
        const formData = new URLSearchParams();
        formData.append('officialAddr', clientConfig.officialAddr);
        formData.append('officialPort', clientConfig.officialPort);
        formData.append('controlToken', clientConfig.controlToken);
        formData.append('bindUsername', clientConfig.bindUsername);
        formData.append('token', "default");

        const response = await fetch(`${API_BASE_URL}/update-local-tunnel-client`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: formData.toString()
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()

        if (data.errCode !== 0) {
          throw new Error(data.errMsg || t('officialClient.startClientFailed'))
        }

        clientConfig.officialSwitch = true
        clientConfig.status = t('officialClient.connected')
        ElMessage.success(t('officialClient.clientStarted'))

        refreshData()
      } catch (error) {
        ElMessage.error(t('officialClient.startFailed') + error.message)
      } finally {
        dataLoading.value = false
      }
    }

    // 停止客户端连接
    const stopClient = async () => {
      try {
        await ElMessageBox.confirm(
          t('officialClient.confirmStopClient'),
          t('officialClient.warning'),
          {
            confirmButtonText: t('officialClient.confirmText'),
            cancelButtonText: t('officialClient.cancelText'),
            type: 'warning',
            confirmButtonClass: 'el-button--danger'
          }
        );
      } catch (error) {
        return;
      }

      dataLoading.value = true
      try {
        const response = await fetch(`${API_BASE_URL}/stop-local-tunnel-client`, {
          method: 'POST',
          credentials: 'include'
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()

        if (data.errCode !== 0) {
          throw new Error(data.errMsg || t('officialClient.stopClientFailed'))
        }

        clientConfig.officialSwitch = false
        clientConfig.status = t('officialClient.notConnected')
        ElMessage.success(t('officialClient.clientStopped'))

        refreshData()
      } catch (error) {
        ElMessage.error(t('officialClient.stopFailed') + error.message)
      } finally {
        dataLoading.value = false
      }
    }

    // 显示日志对话框
    const showLogDialog = () => {
      logDialogVisible.value = true
      fetchLogs()
      // 开启日志自动刷新（如果开启状态）
      if (autoRefreshEnabled.value) {
        startLogAutoRefresh()
      }
    }

    // 切换自动刷新
    const toggleAutoRefresh = (enabled) => {
      if (enabled) {
        startLogAutoRefresh()
        ElMessage.success(t('officialClient.autoRefreshEnabled'))
      } else {
        stopLogAutoRefresh()
        ElMessage.info(t('officialClient.autoRefreshDisabled'))
      }
    }

    // 启动日志自动刷新
    const startLogAutoRefresh = () => {
      stopLogAutoRefresh() // 先停止已有的定时器
      logRefreshInterval.value = setInterval(() => {
        if (logDialogVisible.value) {
          fetchLogs(false) // 静默刷新，不显示加载状态
        }
      }, 5000)
    }

    // 停止日志自动刷新
    const stopLogAutoRefresh = () => {
      if (logRefreshInterval.value) {
        clearInterval(logRefreshInterval.value)
        logRefreshInterval.value = null
      }
    }

    // 获取日志
    const fetchLogs = async (showLoading = true) => {
      try {
        if (showLoading) {
          logLoading.value = true
        }

        const params = new URLSearchParams({
          module: 'controlclient',
          page: logCurrentPage.value,
          size: logPageSize.value
        })

        const response = await fetch(`${API_BASE_URL}/get-moduleLog?${params.toString()}`, {
          credentials: 'include'
        })

        if (!response.ok) throw new Error(t('common.networkError'))

        const data = await response.json()

        if (data.errCode === 1000) {
          ElMessage.warning(t('common.notLoggedIn'))
          router.push('/user-login?callback=official-client')
          return
        }

        if (data.errCode !== 0) {
          throw new Error(data.errMsg || t('officialClient.fetchLogsFailed'))
        }

        logsData.value = data.data || []
        logTotal.value = data.count || 0

        // 只有在非自动刷新（即手动操作）时才滚动到顶部
        if (showLoading && logContentRef.value) {
          logContentRef.value.scrollTop = 0
        }
      } catch (error) {
        console.error(t('officialClient.fetchLogsFailed'), error)
        if (showLoading) {
          ElMessage.error(t('officialClient.fetchLogsError') + error.message)
        }
      } finally {
        if (showLoading) {
          logLoading.value = false
        }
      }
    }

    // 刷新日志
    const refreshLogs = () => {
      fetchLogs(true)
    }

    // 清空日志
    const clearLogs = async () => {
      try {
        await ElMessageBox.confirm(t('officialClient.confirmClearLogs'), t('officialClient.prompt'), {
          confirmButtonText: t('officialClient.confirmText'),
          cancelButtonText: t('officialClient.cancelText'),
          type: 'warning'
        })

        logClearing.value = true

        const params = new URLSearchParams({
          module: 'controlclient'
        })

        const response = await fetch(`${API_BASE_URL}/clear-moduleLog?${params.toString()}`, {
          method: 'DELETE',
          credentials: 'include'
        })

        if (!response.ok) throw new Error(t('common.networkError'))

        const data = await response.json()

        if (data.errCode === 1000) {
          ElMessage.warning(t('common.notLoggedIn'))
          router.push('/user-login?callback=official-client')
          return
        }

        if (data.errCode !== 0) {
          throw new Error(data.errMsg || t('officialClient.clearLogsFailed'))
        }

        ElMessage.success(t('officialClient.logsCleared'))
        fetchLogs(true)
      } catch (error) {
        if (error !== 'cancel') {
          console.error(t('officialClient.clearLogsFailed'), error)
          ElMessage.error(t('officialClient.clearLogsError') + error.message)
        }
      } finally {
        logClearing.value = false
      }
    }

    // 下载所有日志
    const downloadAllLogs = async () => {
      try {
        logDownloading.value = true

        // 获取所有日志（设置一个足够大的size值）
        const params = new URLSearchParams({
          module: 'controlclient',
          page: 1,
          size: 10000 // 设置一个较大的数值，获取尽可能多的日志
        })

        const response = await fetch(`${API_BASE_URL}/get-moduleLog?${params.toString()}`, {
          credentials: 'include'
        })

        if (!response.ok) throw new Error(t('common.networkError'))

        const data = await response.json()

        if (data.errCode === 1000) {
          ElMessage.warning(t('common.notLoggedIn'))
          router.push('/user-login?callback=official-client')
          return
        }

        if (data.errCode !== 0) {
          throw new Error(data.errMsg || t('officialClient.fetchLogsFailed'))
        }

        const allLogs = data.data || []

        if (allLogs.length === 0) {
          ElMessage.warning(t('officialClient.noLogsToDownload'))
          return
        }

        // 格式化所有日志内容
        let logContent = ''
        allLogs.forEach(log => {
          logContent += `[${log.Time}] [${log.Level}] ${log.Content}\n`
        })

        // 创建下载链接
        const blob = new Blob([logContent], { type: 'text/plain;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `tunnel_mapping_logs_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.log`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)

        ElMessage.success(t('officialClient.downloadSuccess', { count: allLogs.length }))
      } catch (error) {
        console.error(t('officialClient.downloadFailed'), error)
        ElMessage.error(t('officialClient.downloadError') + error.message)
      } finally {
        logDownloading.value = false
      }
    }

    // 处理每页条数变化
    const handleLogSizeChange = (size) => {
      logPageSize.value = size
      logCurrentPage.value = 1
      fetchLogs(true)
    }

    // 处理页码变化
    const handleLogPageChange = (page) => {
      logCurrentPage.value = page
      fetchLogs(true)
    }
    
    // 窗口大小变化处理
    const handleResize = () => {
      checkMobile()
    }
    
    onMounted(() => {
      checkMobile()
      window.addEventListener('resize', handleResize)
      refreshData()
      startTimer()
    })
    
    onUnmounted(() => {
      window.removeEventListener('resize', handleResize)
      stopTimer()
      stopLogAutoRefresh()
    })
    
    return {
      isMobile,
      dataLoading,
      error,
      clientConfig,
      mappingList,
      connectServerIP,
      clientDialogVisible,
      mappingDialogVisible,
      mappingDialogType,
      clientEditForm,
      mappingEditForm,
      clientFormRef,
      mappingFormRef,
      clientRules,
      mappingRules,
      showClientEditDialog,
      showAddMappingDialog,
      showMappingEditDialog,
      handleClientDialogClose,
      resetClientForm,
      resetMappingForm,
      refreshData,
      updateMapping,
      stopMapping,
      saveMapping,
      updateClient,
      startClient,
      stopClient,
      // 日志相关
      logDialogVisible,
      logLoading,
      logClearing,
      logDownloading,
      logsData,
      logTotal,
      logCurrentPage,
      logPageSize,
      logContentRef,
      autoRefreshEnabled,
      showLogDialog,
      toggleAutoRefresh,
      refreshLogs,
      clearLogs,
      downloadAllLogs,
      handleLogSizeChange,
      handleLogPageChange
    }
  }
}
</script>

<style scoped>
.port-config-page {
  padding: 0px;
  max-width: 100%;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
  background-color: #f5f7fa;
  min-height: 100vh;
}

@media (max-width: 768px) {
  .port-config-page {
    padding: 12px;
  }
}

@media (max-width: 480px) {
  .port-config-page {
    padding: 8px;
  }
}

.tunnel-card, .mapping-card {
  margin-bottom: 20px;
  border-radius: 8px;
  overflow: hidden;
  width: 100%;
  box-sizing: border-box;
}

@media (max-width: 768px) {
  .tunnel-card, .mapping-card {
    margin-bottom: 16px;
  }
}

.skeleton-card {
  margin-bottom: 20px;
  padding: 20px;
  background-color: white;
  border-radius: 8px;
}

.error-alert {
  margin-bottom: 20px;
  border-radius: 8px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.header-actions {
  display: flex;
  gap: 8px;
}

@media (max-width: 768px) {
  .card-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .header-actions {
    width: 100%;
    justify-content: flex-end;
  }
}

.table-responsive {
  overflow-x: auto;
  width: 100%;
}

@media (max-width: 768px) {
  .table-responsive {
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
}

.config-table,
.mapping-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.config-table th,
.config-table td,
.mapping-table th,
.mapping-table td {
  padding: 12px 15px;
  text-align: left;
  border-bottom: 1px solid #ebeef5;
}

.config-table th,
.mapping-table th {
  background-color: #f5f7fa;
  color: #909399;
  font-weight: 600;
  white-space: nowrap;
}

.config-table td,
.mapping-table td {
  color: #606266;
  vertical-align: middle;
}

.config-table tr:hover td,
.mapping-table tr:hover td {
  background-color: #f5f7fa;
}

@media (max-width: 768px) {
  .config-table,
  .mapping-table {
    width: 100%;
    min-width: 100%;
    display: block;
  }
  
  .config-table thead,
  .mapping-table thead {
    display: none;
  }
  
  .config-table tbody,
  .config-table tr,
  .mapping-table tbody,
  .mapping-table tr {
    width: 100%;
    display: block;
  }
  
  .config-table tr,
  .mapping-table tr {
    margin-bottom: 16px;
    border: 1px solid #ebeef5;
    border-radius: 4px;
    background: #fff;
  }
  
  .config-table td,
  .mapping-table td {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 12px;
    border-bottom: 1px solid #ebeef5;
    width: 100%;
    box-sizing: border-box;
  }
  
  .config-table td:before,
  .mapping-table td:before {
    content: attr(data-label);
    font-weight: 600;
    color: #909399;
    margin-right: 12px;
    flex-shrink: 0;
    min-width: 80px;
    font-size: 13px;
  }
  
  .config-table td:last-child,
  .mapping-table td:last-child {
    border-bottom: none;
  }
  
  .config-table td.action-cell,
  .mapping-table td.action-cell {
    padding: 10px 12px;
  }
  
  .config-table td.action-cell:before,
  .mapping-table td.action-cell:before {
    align-self: flex-start;
    padding-top: 4px;
  }
}

@media (max-width: 480px) {
  .config-table td,
  .mapping-table td {
    padding: 8px 10px;
    font-size: 12px;
  }
  
  .config-table td:before,
  .mapping-table td:before {
    min-width: 70px;
    font-size: 12px;
  }
}

.status-tag {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.status-tag.enabled {
  background-color: #f0f9eb;
  color: #67c23a;
}

.status-tag.disabled {
  background-color: #f5f5f5;
  color: #909399;
}

.protocol-tag {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.protocol-tag.tcp {
  background-color: #ecf5ff;
  color: #409eff;
}

.protocol-tag.udp {
  background-color: #fdf6ec;
  color: #e6a23c;
}

.status-wrapper {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.status-message {
  font-size: 12px;
  color: #606266;
  word-break: break-all;
}

.action-cell .action-buttons {
  display: flex;
  gap: 4px;
  flex-wrap: nowrap;
}

.action-cell .action-buttons .el-button {
  flex-shrink: 0;
}

.action-cell .action-buttons .el-button + .el-button {
  margin-left: 0;
}

@media (max-width: 768px) {
  .action-cell .action-buttons {
    justify-content: flex-end;
  }
  
  .action-cell .el-button--small {
    padding: 4px 8px;
    font-size: 12px;
  }
}

@media (max-width: 480px) {
  .action-cell .action-buttons {
    gap: 2px;
  }
  
  .action-cell .el-button--small {
    padding: 4px 6px;
    font-size: 11px;
  }
}

.empty-mapping {
  padding: 40px 0;
}

.form-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
}

/* 日志对话框样式 */
.log-dialog :deep(.el-dialog__body) {
  padding: 20px;
  max-height: 70vh;
  overflow-y: auto;
}

.log-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #ebeef5;
}

.log-toolbar .log-stats {
  display: flex;
  align-items: center;
  gap: 24px;
  font-size: 13px;
  color: #606266;
}

.log-toolbar .log-stats .auto-refresh-control {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-left: 16px;
  border-left: 1px solid #dcdfe6;
}

.log-toolbar .log-stats .auto-refresh-control .refresh-label {
  font-size: 12px;
  color: #909399;
}

.log-toolbar .log-stats .auto-refresh-control .refresh-interval {
  font-size: 12px;
  color: #67c23a;
}

.log-toolbar .log-actions {
  display: flex;
  gap: 8px;
}

@media (max-width: 768px) {
  .log-toolbar {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }
  
  .log-toolbar .log-stats {
    width: 100%;
    flex-wrap: wrap;
    gap: 12px;
  }
  
  .log-toolbar .log-stats .auto-refresh-control {
    padding-left: 0;
    border-left: none;
  }
  
  .log-toolbar .log-actions {
    width: 100%;
    justify-content: flex-end;
  }
}

@media (max-width: 480px) {
  .log-toolbar .log-stats {
    font-size: 12px;
  }
}

.log-content {
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background-color: #fafafa;
  margin-bottom: 16px;
  max-height: 400px;
  overflow-y: auto;
}

.log-content .empty-log {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #909399;
}

.log-content .empty-log .el-icon {
  font-size: 32px;
  margin-bottom: 12px;
}

.log-content .empty-log p {
  margin: 0;
  font-size: 14px;
}

.log-content .log-list {
  padding: 12px;
}

.log-content .log-list .log-item {
  padding: 8px 12px;
  border-bottom: 1px solid #e4e7ed;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 13px;
  line-height: 1.5;
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.log-content .log-list .log-item:last-child {
  border-bottom: none;
}

.log-content .log-list .log-item.log-level-error {
  background-color: #fef0f0;
}

.log-content .log-list .log-item.log-level-warn {
  background-color: #fdf6ec;
}

.log-content .log-list .log-item .log-time {
  color: #909399;
  font-size: 12px;
  white-space: nowrap;
  flex-shrink: 0;
}

.log-content .log-list .log-item .log-level {
  font-weight: 600;
  font-size: 12px;
  white-space: nowrap;
  flex-shrink: 0;
}

.log-content .log-list .log-item .log-level.level-error {
  color: #f56c6c;
}

.log-content .log-list .log-item .log-level.level-warn {
  color: #e6a23c;
}

.log-content .log-list .log-item .log-level.level-info {
  color: #409eff;
}

.log-content .log-list .log-item .log-message {
  color: #303133;
  word-break: break-all;
  flex: 1;
}

@media (max-width: 768px) {
  .log-content {
    max-height: 350px;
  }
  
  .log-content .log-list {
    padding: 8px;
  }
  
  .log-content .log-list .log-item {
    font-size: 12px;
    flex-wrap: wrap;
    gap: 4px;
  }
  
  .log-content .log-list .log-item .log-time {
    width: 100%;
    margin-bottom: 2px;
  }
  
  .log-content .log-list .log-item .log-message {
    width: 100%;
    margin-left: 0;
  }
}

@media (max-width: 480px) {
  .log-content {
    max-height: 300px;
  }
  
  .log-content .log-list .log-item {
    font-size: 11px;
  }
}

.log-pagination {
  display: flex;
  justify-content: flex-end;
}

@media (max-width: 768px) {
  .log-pagination {
    justify-content: center;
  }
}

:deep(.el-dialog__body) {
  padding-top: 20px;
  padding-bottom: 10px;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

@media (max-width: 480px) {
  .dialog-footer {
    flex-direction: column;
  }
  
  .dialog-footer .el-button {
    width: 100%;
    margin-left: 0 !important;
  }
}

/* Element Plus 对话框移动端适配 */
.settings-dialog :deep(.el-dialog) {
  margin: 0 auto;
}

@media (max-width: 768px) {
  .settings-dialog :deep(.el-dialog) {
    width: 95% !important;
    margin-top: 5vh !important;
  }
}

@media (max-width: 480px) {
  .settings-dialog :deep(.el-dialog) {
    width: 100% !important;
    height: auto !important;
    margin: 0 !important;
    max-height: 100%;
    border-radius: 0;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    margin-bottom: 0 !important;
  }
  
  .settings-dialog :deep(.el-dialog__header) {
    padding: 12px 16px;
    border-bottom: 1px solid #ebeef5;
  }
  
  .settings-dialog :deep(.el-dialog__body) {
    padding: 16px;
    max-height: calc(80vh - 120px);
    overflow-y: auto;
  }
  
  .settings-dialog :deep(.el-dialog__footer) {
    padding: 12px 16px;
    border-top: 1px solid #ebeef5;
  }
}

/* Element Plus 组件适配 */
:deep(.el-input__inner) {
  width: 100%;
}

@media (max-width: 768px) {
  :deep(.el-input__inner) {
    font-size: 13px;
    height: 32px;
    line-height: 32px;
  }
  
  :deep(.el-input-number) {
    width: 100% !important;
  }
  
  :deep(.el-input-number .el-input__inner) {
    text-align: left;
  }
}

@media (max-width: 480px) {
  :deep(.el-input__inner) {
    font-size: 12px;
    height: 30px;
    line-height: 30px;
  }
}

:deep(.el-form-item__label) {
  font-weight: 500;
}

@media (max-width: 768px) {
  :deep(.el-form-item__label) {
    font-size: 13px;
    padding-bottom: 4px;
  }
}

@media (max-width: 480px) {
  :deep(.el-form-item__label) {
    font-size: 12px;
  }
}

:deep(.el-button--small) {
  padding: 6px 12px;
}

@media (max-width: 768px) {
  :deep(.el-button--small) {
    padding: 6px 10px;
    font-size: 12px;
  }
}

@media (max-width: 480px) {
  :deep(.el-button--small) {
    padding: 5px 8px;
    font-size: 11px;
  }
}

:deep(.el-radio) {
  margin-right: 16px;
}

@media (max-width: 480px) {
  :deep(.el-radio) {
    display: block;
    margin-bottom: 8px;
  }
}

:deep(.el-switch) {
  @media (max-width: 768px) {
    .el-switch__core {
      width: 36px !important;
      height: 18px !important;
      
      &:after {
        width: 14px !important;
        height: 14px !important;
      }
    }
  }
}
</style>